import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import Select from "react-select";
import {
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaShoppingCart,
  FaPlusCircle,
  FaTrashAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl } from "../../baseurl";

function AddInvoice() {
  const [invoice, setInvoice] = useState({
    fullName: "",
    phone: "",
    address: "",
    date: new Date(),
    paymentMode: { value: "cash", label: "Cash" },
    cashAmount: "",
    upiAmount: "",
  });

  const [items, setItems] = useState([
    { item: null, qty: 1, amt: "", discount: 0, total: 0 },
  ]);

  const [medicineOptions, setMedicineOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const paymentOptions = [
    { value: "cash", label: "Cash" },
    { value: "upi", label: "UPI" },
    { value: "cash+upi", label: "Cash + UPI" },
  ];

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "10px",
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(13,110,253,0.2)" : "none",
      "&:hover": { borderColor: "#0d6efd" },
      minHeight: "42px",
    }),
  };

  // ✅ Fetch medicines from API
  useEffect(() => {
    const fetchMedicines = async () => {
      try {
        const res = await axios.get(`${baseurl}getMedicineSummary`);
        if (res.data.success) {
          const formatted = res.data.data.map((med) => ({
            value: med.id,
            label: `${med.name} [Stock: ${med.stock || 0}, Exp: ${
              med.expiry_date || "N/A"
            }]`,
            stock: med.stock || 0,
            selling_price: parseFloat(med.selling_price),
          }));
          setMedicineOptions(formatted);
        }
      } catch (err) {
        console.error("Error fetching medicines:", err);
        Swal.fire("Error", "Failed to load medicine list.", "error");
      }
    };
    fetchMedicines();
  }, []);

  // 🧾 Handle invoice input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoice({ ...invoice, [name]: value });
  };

  const handleDateChange = (date) => {
    setInvoice({ ...invoice, date: date[0] });
  };

  const handleSelectChange = (selectedOption) => {
    setInvoice({
      ...invoice,
      paymentMode: selectedOption,
      cashAmount: "",
      upiAmount: "",
    });
  };

  // ✅ Handle medicine selection per row
 const handleMedicineSelect = (index, selectedMedicine) => {
  if (selectedMedicine && selectedMedicine.stock === 0) {
    Swal.fire("Out of Stock", "This medicine is currently unavailable.", "warning");
    return;
  }

  const updatedItems = [...items];
  updatedItems[index].item = selectedMedicine;

  updatedItems[index].qty = 1;
  updatedItems[index].amt = selectedMedicine ? selectedMedicine.selling_price : "";
  updatedItems[index].discount = 0;
  updatedItems[index].total = selectedMedicine ? selectedMedicine.selling_price : 0;

  setItems(updatedItems);
};


  // ✅ Handle row input (qty, amt, discount)
const handleItemChange = (index, field, value) => {
  const updatedItems = [...items];
  let newValue = value;

  // ✅ Check Stock Limit When Qty Changes
  if (field === "qty") {
    const selectedMed = updatedItems[index].item;

    if (selectedMed) {
      const stock = selectedMed.stock || 0;

      if (parseInt(value) > stock) {
        Swal.fire(
          "Stock Limit Exceeded",
          `Only ${stock} units available in stock.`,
          "warning"
        );
        newValue = stock; // Auto-set max qty = stock
      }

      if (parseInt(value) < 1) newValue = 1; // Avoid zero/negative qty
    }
  }

  updatedItems[index][field] = newValue;

  const qty = parseFloat(updatedItems[index].qty || 0);
  const amt = parseFloat(updatedItems[index].amt || 0);
const discountPercent = parseFloat(updatedItems[index].discount || 0);
const discountAmount = (qty * amt * discountPercent) / 100;
updatedItems[index].total = qty * amt - discountAmount;
  setItems(updatedItems);
};

  const addRow = () => {
    setItems([...items, { item: null, qty: 1, amt: "", discount: 0, total: 0 }]);
  };

  const removeRow = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.total || 0), 0);

  // ✅ Submit invoice
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🧮 Validation for Cash + UPI
    if (invoice.paymentMode.value === "cash+upi") {
      const cash = parseFloat(invoice.cashAmount || 0);
      const upi = parseFloat(invoice.upiAmount || 0);
      if (cash + upi !== totalAmount) {
        Swal.fire(
          "Validation Error",
          `Cash + UPI amount must equal ₹${totalAmount.toFixed(2)}.`,
          "warning"
        );
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        ...invoice,
        date: invoice.date ? invoice.date.toISOString().split("T")[0] : null,
        paymentMode: invoice.paymentMode?.value,
        cashAmount:
          invoice.paymentMode.value === "cash+upi"
            ? parseFloat(invoice.cashAmount || 0)
            : invoice.paymentMode.value === "cash"
            ? totalAmount
            : 0,
        upiAmount:
          invoice.paymentMode.value === "cash+upi"
            ? parseFloat(invoice.upiAmount || 0)
            : invoice.paymentMode.value === "upi"
            ? totalAmount
            : 0,
        items: items.map((it) => ({
          medicine_id: it.item?.value,
          medicine_name: it.item?.label,
          qty: it.qty,
          amt: it.amt,
          discount: it.discount,
          total: it.total,
        })),
        totalAmount,
      };

      console.log("🧾 Invoice Payload:", payload);

      const res = await axios.post(`${baseurl}addInvoice`, payload);
      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Invoice Added",
          text: "Invoice saved successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        setInvoice({
          fullName: "",
          phone: "",
          address: "",
          date: new Date(),
          paymentMode: { value: "cash", label: "Cash" },
          cashAmount: "",
          upiAmount: "",
        });
        setItems([{ item: null, qty: 1, amt: "", discount: 0, total: 0 }]);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res.data.message || "Failed to save invoice.",
        });
      }
    } catch (err) {
      console.error("Error:", err);
      Swal.fire(
        "Request Failed",
        err.response?.data?.message || "Something went wrong!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };


const [suggestedAddress, setSuggestedAddress] = useState("");
const [suggestedName, setSuggestedName] = useState("");


const handlePhoneChange = async (e) => {
  const value = e.target.value;

  setInvoice({ ...invoice, phone: value });

  if (value.length === 10) {
    try {
      const res = await axios.get(`${baseurl}getAddressByPhone/${value}`);

      if (res.data.success) {
        setSuggestedAddress(res.data.address || "");
        setSuggestedName(res.data.full_name || "");
      } else {
        setSuggestedAddress("");
        setSuggestedName("");
      }

    } catch (err) {
      console.error("Error fetching address:", err);
      setSuggestedAddress("");
      setSuggestedName("");
    }
  } else {
    setSuggestedAddress("");
    setSuggestedName("");
  }
};






  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="container py-4">
          <div className="card border-0 shadow-lg rounded-4">
            <div className="card-header text-white rounded-top-4 ">
              <h4 className="mb-0 fw-bold d-flex align-items-center gap-2">
                <FaShoppingCart /> Add New Invoice
              </h4>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                {/* Customer Info */}
                <div className="row g-3 mb-4">
                 <div className="col-md-4">
  <label className="form-label fw-semibold">
    <FaUser className="me-2 text-primary" />
    Full Name
  </label>

  <input
    type="text"
    name="fullName"
    value={invoice.fullName}
    onChange={handleInputChange}
    className="form-control form-control-lg"
    placeholder="Enter customer name"
    required
  />

  {/* 🟡 Suggested Full Name */}
  {suggestedName && invoice.fullName.trim() === "" && (
    <div
      className="p-2 mt-1 border rounded bg-light suggestion-box"
      style={{ cursor: "pointer" }}
      onClick={() => {
        setInvoice({ ...invoice, fullName: suggestedName });
        setSuggestedName(""); // hide after select
      }}
    >
      <strong>Suggested:</strong> {suggestedName}
    </div>
  )}
</div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      <FaPhone className="me-2 text-primary" />
                      Phone Number
                    </label>
                   <input
  type="tel"
  name="phone"
  value={invoice.phone}
  onChange={handlePhoneChange}
  className="form-control form-control-lg"
  placeholder="e.g. 9876543210"
  required
/>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      <FaCalendarAlt className="me-2 text-primary" />
                      Invoice Date
                    </label>
                    <Flatpickr
                      value={invoice.date}
                      onChange={handleDateChange}
                      options={{ dateFormat: "Y-m-d" }}
                      className="form-control form-control-lg"
                    />
                  </div>

                  <div className="col-md-8">
  <label className="form-label fw-semibold">
    <FaMapMarkerAlt className="me-2 text-primary" />
    Address
  </label>

  <textarea
    name="address"
    value={invoice.address}
    onChange={handleInputChange}
    className="form-control"
    rows="2"
    placeholder="Enter address"
  ></textarea>

  {/* 🟡 SUGGESTION BOX */}
  {suggestedAddress && invoice.address.trim() === "" && (
    <div
      className="p-2 mt-1 border rounded bg-light suggestion-box"
      style={{ cursor: "pointer" }}
      onClick={() => {
        setInvoice({ ...invoice, address: suggestedAddress });
        setSuggestedAddress(""); // Hide suggestion after selecting
      }}
    >
      <strong>Suggested:</strong> {suggestedAddress}
    </div>
  )}
</div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      <FaRupeeSign className="me-2 text-primary" />
                      Payment Mode
                    </label>
                    <Select
                      options={paymentOptions}
                      value={invoice.paymentMode}
                      onChange={handleSelectChange}
                      styles={customSelectStyles}
                      placeholder="Select mode"
                    />
                  </div>

                  {/* Conditional Fields for Cash + UPI */}
                  {invoice.paymentMode.value === "cash+upi" && (
                    <>
                      <div className="col-md-2">
                        <label className="form-label fw-semibold">
                          Cash Amount
                        </label>
                        <input
                          type="number"
                          name="cashAmount"
                          value={invoice.cashAmount}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="₹ Cash"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-semibold">
                          UPI Amount
                        </label>
                        <input
                          type="number"
                          name="upiAmount"
                          value={invoice.upiAmount}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="₹ UPI"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Items Section */}
                <div className="table-responsive">
                  <table className="table table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th style={{ width: "100px" }}>Qty</th>
                        <th style={{ width: "120px" }}>Amount</th>
                        <th style={{ width: "120px" }}>Discount %</th>
                        <th style={{ width: "120px" }}>Total</th>
                        <th style={{ width: "80px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row, index) => (
                        <tr key={index}>
                          <td style={{ minWidth: "200px" }}>
                            <Select
                              classNamePrefix="my-select"
                              options={medicineOptions}
                              value={row.item}
                              onChange={(val) =>
                                handleMedicineSelect(index, val)
                              }
                              placeholder="Select medicine"
                              isSearchable
                              menuPlacement="auto"
                              menuPosition="fixed"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                              formatOptionLabel={(option) => (
                                <div
                                  style={{
                                    color:
                                      option.stock === 0 ? "red" : "black",
                                    fontWeight:
                                      option.stock === 0 ? "600" : "normal",
                                  }}
                                >
                                  {option.label}
                                </div>
                              )}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              value={row.qty}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "qty",
                                  e.target.value
                                )
                              }
                              className="form-control text-center"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.amt}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "amt",
                                  e.target.value
                                )
                              }
                              className="form-control text-center"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.discount}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "discount",
                                  e.target.value
                                )
                              }
                              className="form-control text-center"
                            />
                          </td>
                          <td className="text-center fw-semibold">
                            ₹{row.total.toFixed(2)}
                          </td>
                          <td className="text-center">
                            {items.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => removeRow(index)}
                              >
                                <FaTrashAlt />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-primary rounded-3"
                    onClick={addRow}
                  >
                    <FaPlusCircle className="me-1" /> Add Row
                  </button>

                  <h5 className="fw-bold">
                    Total Amount:{" "}
                    <span className="text-success">
                      ₹{totalAmount.toFixed(2)}
                    </span>
                  </h5>
                </div>

                <div className="text-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg px-5 py-2 rounded-3 shadow-sm"
                    disabled={loading}
                  >
                    💾 {loading ? "Saving..." : "Save Invoice"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default AddInvoice;
